/**
 * /life-empowerment · Public marketing pages
 * ───────────────────────────────────────────────────────────────────────────
 * 7 page components for the dark-mode LIFE LLC corporate website:
 *   Home · About · Services · Recognition · Contact (+Intake) · Privacy · Terms
 *
 * All content sourced verbatim from the vaulted PDFs
 * (Life Bus Proposal.pdf + LIFE Cost Analysis.pdf) where directly quoted.
 */

import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, Building2, ShieldCheck, Sparkles, Users,
  Factory, Handshake, Clock, Network, BadgeCheck, Send, MapPin, Mail,
  TrendingUp, UploadCloud, Loader2, FileCheck2, CheckCircle2, AlertCircle,
  Briefcase, Quote,
} from "lucide-react";
import { useSeo, defaultOrgJsonLd } from "../../lib/useSeo";
import { ga4Event } from "../../lib/useGA4";
import { LIFE_EMBLEM_URL } from "./LifeEmpowermentLayout";

const API  = `${process.env.REACT_APP_BACKEND_URL}/api/site`;
const ORIG = typeof window !== "undefined" ? window.location.origin : "";

/* ─── Tiny shared UI atoms ─────────────────────────────────────────── */
function Eyebrow({ children, testId }) {
  return (
    <div data-testid={testId} className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#b7092b] mb-3 inline-flex items-center gap-2">
      <span className="h-px w-6 bg-[#d4af37]" /> {children}
    </div>
  );
}
function SectionTitle({ children, testId }) {
  return (
    <h2 data-testid={testId} className="life-serif font-medium text-[36px] sm:text-[44px] lg:text-[52px] text-[#1f3a60] tracking-tight leading-[1.05] mb-5 max-w-3xl">
      {children}
    </h2>
  );
}
function P({ children, className = "" }) {
  return <p className={`text-[#212529]/90 text-[15px] leading-[1.72] max-w-3xl ${className}`}>{children}</p>;
}
function PrimaryCta({ to, href, onClick, children, testId, external }) {
  const cls = "life-cta-gold inline-flex items-center gap-2 px-6 py-3 font-mono text-[10.5px] tracking-[0.24em] uppercase";
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}<ArrowRight className="h-4 w-4"/></Link>;
  if (href) return <a href={href} className={cls} data-testid={testId} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{children}<ArrowUpRight className="h-4 w-4"/></a>;
  return <button onClick={onClick} className={cls} data-testid={testId} type="button">{children}<ArrowRight className="h-4 w-4"/></button>;
}
function GhostCta({ to, children, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="life-cta-ghost inline-flex items-center gap-2 px-6 py-3 font-mono text-[10.5px] tracking-[0.24em] uppercase">
      {children} <ArrowRight className="h-4 w-4"/>
    </Link>
  );
}
function Container({ children, className = "" }) {
  return <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 ${className}`}>{children}</div>;
}

/* ════════════════════════════════════════════════════════════════════
   1. HOME
   ════════════════════════════════════════════════════════════════════ */
export function LifeHome() {
  const { cfg } = useOutletContext() || {};
  useSeo({
    title:       "L.I.F.E., LLC · Workforce Development & Business Consulting · Myrtle Beach, SC",
    description: "Highlighting the human element, then empowering it. Corporate workforce development, professional relationship cultivation, and business consulting from Learning Institute for Empowerment LLC — a South Carolina domestic LLC headquartered in Myrtle Beach.",
    canonical:   `${ORIG}/life-empowerment`,
    jsonLd:      defaultOrgJsonLd(cfg),
  });

  return (
    <div data-testid="life-page-home">
      {/* HERO — center-stage emblem + serif headline */}
      <section className="relative overflow-hidden">
        {/* Cinematic radial glow placed behind the medallion */}
        <div className="absolute inset-0 pointer-events-none life-hero-radial"/>
        {/* Gold filament accents on top + bottom of hero band */}
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#d4af37]/45 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-[#d4af37]/25 to-transparent"/>

        <Container className="relative py-16 lg:py-24 text-center">
          {/* 🛡️ Corporate emblem · transparent-background extract served
              from /life-emblem.png. Rendered at the owner-spec'd
              max-height: 180px / width: auto for the home page hero. */}
          <img
            src={LIFE_EMBLEM_URL}
            alt="Learning Institute for Empowerment corporate emblem"
            data-testid="life-home-emblem"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="life-emblem-3d mx-auto block w-auto mb-8 lg:mb-10"
            style={{
              maxHeight:      "180px",
              height:         "auto",
              width:          "auto",
              objectFit:      "contain",
              imageRendering: "auto",
            }}
          />

          <Eyebrow testId="life-home-eyebrow">A South Carolina Domestic LLC · Est. 2023</Eyebrow>

          <h1
            data-testid="life-home-title"
            className="life-serif font-medium text-[40px] sm:text-[56px] lg:text-[78px] text-[#1f3a60] tracking-tight leading-[1.02] mb-6 mx-auto max-w-5xl italic"
          >
            Highlighting the <span className="text-[#b7092b] not-italic font-semibold">Human Element,</span>
            <br className="hidden sm:block"/>{" "}
            Then <span className="not-italic font-semibold bg-gradient-to-r from-[#d4af37] to-[#c5a059] bg-clip-text text-transparent">Empowering It.</span>
          </h1>

          <P className="text-[17px] lg:text-[19px] mb-10 mx-auto text-center max-w-3xl">
            Transforming what was once a business and community liability into
            an enduring asset. We bridge commerce and community by mining,
            refining, and embedding the <i>Empowered Human Element</i> at the
            core of America's worksites.
          </P>

          <div className="flex flex-wrap gap-3 items-center justify-center">
            <PrimaryCta to="/life-empowerment/contact" testId="life-home-cta-partner">Secure Corporate Partnership</PrimaryCta>
            <GhostCta to="/life-empowerment/contact" testId="life-home-cta-intake">Contractor Intake Application</GhostCta>
          </div>
        </Container>
      </section>

      {/* THREE-PILLAR STRIP */}
      <section className="life-pillars">
        <Container className="grid md:grid-cols-3 gap-0">
          {[
            { Icon: Building2,   k: "B2B Workforce",         v: "Empowered Human Elements supplied directly to corporate worksites." },
            { Icon: ShieldCheck, k: "IRC §117 Stipend",      v: "Non-taxable vocational training award — gross = net, no withholdings." },
            { Icon: BadgeCheck,  k: "Audit-Compliant",       v: "Every paycheck routed through the Pending Vendor Check Registry." },
          ].map(({ Icon, k, v }, i) => (
            <div key={k} className={`p-6 lg:p-8 ${i < 2 ? "md:border-r border-[#dee2e6]" : ""}`}>
              <Icon className="h-6 w-6 text-[#d4af37] mb-3" strokeWidth={1.5}/>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] mb-1">{k}</div>
              <p className="text-[#212529]/85 text-[14px] leading-relaxed">{v}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* MISSION CALLOUT */}
      <section className="py-20 lg:py-28">
        <Container className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>Mission</Eyebrow>
            <SectionTitle>From an unguided destructive energy<br/>to a constructive force.</SectionTitle>
          </div>
          <blockquote className="life-card relative p-7 lg:p-9">
            <Quote className="absolute -top-3 -left-3 h-7 w-7 text-[#d4af37] bg-white p-1 rounded-sm shadow-sm"/>
            <p className="text-[#1f3a60] text-[17px] leading-[1.78]">
              "Our purpose and singular motivation is to highlight the human
              element, then empower it. We will inspire it, transform it,
              restructure its chemistry from an unguided destructive energy to
              a constructive force. We will turn what was once a business and
              community liability and make it an asset. It will be an
              ever-enduring legacy of success."
            </p>
            <cite className="not-italic mt-5 block font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b]/85">
              — Mission Statement, L.I.F.E. Business Proposal
            </cite>
          </blockquote>
        </Container>
      </section>

      {/* CTA BAND */}
      <section className="relative bg-[#f8f9fa] border-t border-[#dee2e6]">
        <Container className="py-16 lg:py-24 text-center">
          <Eyebrow>Ready to engage</Eyebrow>
          <h2 className="life-serif font-medium italic text-[36px] sm:text-[44px] lg:text-[54px] text-[#1f3a60] tracking-tight leading-[1.04] mb-5 mx-auto max-w-3xl">
            Two ways to <span className="not-italic font-semibold text-[#b7092b]">begin.</span>
          </h2>
          <P className="mx-auto text-center mb-8 max-w-2xl">
            Hiring managers and CHROs — open a partnership. Vocational trainees
            and program participants — submit an intake. Both routes are
            reviewed personally by the Senior Member.
          </P>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <PrimaryCta to="/life-empowerment/contact" testId="life-home-cta-band-partner">Secure Corporate Partnership</PrimaryCta>
            <GhostCta to="/life-empowerment/contact" testId="life-home-cta-band-intake">Contractor Intake Application</GhostCta>
          </div>
        </Container>
      </section>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   2. ABOUT
   ════════════════════════════════════════════════════════════════════ */
export function LifeAbout() {
  const { cfg } = useOutletContext() || {};
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    axios.get(`${API}/life-public/leadership`).then((r) => setLeaders(r.data?.leadership || [])).catch(() => {});
  }, []);
  useSeo({
    title:       "About · Learning Institute for Empowerment LLC",
    description: "Our purpose and singular motivation is to highlight the human element, then empower it — and reverse the decline of business efficiency to rejuvenate commerce. Read the L.I.F.E. mission, philosophy, and leadership.",
    canonical:   `${ORIG}/life-empowerment/about`,
    jsonLd:      defaultOrgJsonLd(cfg),
  });

  return (
    <div data-testid="life-page-about">
      <Container className="py-16 lg:py-24">
        <Eyebrow>About L.I.F.E., LLC</Eyebrow>
        <h1 data-testid="life-about-title" className="life-serif font-medium italic text-[42px] sm:text-[56px] lg:text-[72px] text-[#1f3a60] tracking-tight leading-[1.05] mb-6 max-w-4xl">
          A bridge between <span className="text-[#d4af37] not-italic font-semibold">commerce</span> and <span className="text-[#b7092b] not-italic font-semibold">community.</span>
        </h1>
        <P className="text-[17px] mb-8">
          Imagine those individuals within our community who are not afforded
          conventional educational opportunities, or the ability to properly
          prepare for life challenges. What are their odds to navigate the
          landscape of success? The Learning Institute for Empowerment exists
          to answer that question — by discovering and refining the untapped
          resource that gives every individual the opportunity to succeed.
        </P>
      </Container>

      {/* MISSION STATEMENT — verbatim */}
      <Container className="pb-16">
        <div className="life-card p-7 lg:p-10 relative">
          <div className="absolute -top-3 left-6 px-3 py-1 bg-[#b7092b] text-white font-mono text-[10px] tracking-[0.22em] uppercase shadow-sm">
            Mission Statement
          </div>
          <p data-testid="life-about-mission" className="text-[#1f3a60] text-[17px] lg:text-[18px] leading-[1.78]">
            Our purpose and singular motivation is to highlight the human
            element, then empower it. We will inspire it, transform it,
            restructure its chemistry from an unguided destructive energy to a
            constructive force. We will turn what was once a business and
            community liability and make it an asset. It will be an
            ever-enduring legacy of success. We will utilize the untapped
            resource of the empowered human element — and fuel commerce for
            millennia.
          </p>
        </div>
      </Container>

      {/* PHILOSOPHY + DESCRIPTION TWO-COL */}
      <Container className="grid lg:grid-cols-2 gap-10 lg:gap-14 pb-16">
        <div>
          <Eyebrow>Business Philosophy</Eyebrow>
          <SectionTitle>The Empowered Human Element is the catalyst of every success.</SectionTitle>
          <P>
            Developing and cultivating the empowered human element is paramount.
            Those at the helm of upward-mobile companies look beyond standard
            components to succeed — they look for a good source of the human
            element. Every business, from sole proprietorship to corporation,
            has it at its heart. Every community has an ample supply. Now is
            the time to empower them. L.I.F.E. is the foundation that has taken
            on this moral responsibility.
          </P>
        </div>
        <div>
          <Eyebrow>Business Description</Eyebrow>
          <SectionTitle>An efficient network between community and commerce.</SectionTitle>
          <P>
            The demand for an empowered human element is met by mining and
            refining that element. We establish an efficient network between
            community and commerce — starting on the local level, expanding
            globally. The infrastructure that we create is essential for
            sustainable economic success for generations to come. The
            implementation of the empowered human element reverses the decline
            of our communities and rejuvenates every aspect of our lives.
          </P>
        </div>
      </Container>

      {/* LEADERSHIP */}
      <section className="bg-[#f8f9fa] border-y border-[#dee2e6] py-16 lg:py-24">
        <Container>
          <Eyebrow>Leadership</Eyebrow>
          <SectionTitle>Senior members of the Institute.</SectionTitle>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {leaders.length === 0 && (
              <div className="text-[#1f3a60]/50 font-mono text-[11px] tracking-[0.18em] uppercase">
                Loading leadership profiles…
              </div>
            )}
            {leaders.map((l) => (
              <article
                key={l.id}
                data-testid={`life-leader-${(l.name||"").toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
                className="life-card p-6"
              >
                <div className="h-14 w-14 grid place-items-center bg-gradient-to-br from-[#d4af37] to-[#c5a059] text-[#1f3a60] mb-4 shadow-sm">
                  <Users className="h-7 w-7"/>
                </div>
                <h3 className="font-display font-bold text-[#1f3a60] text-xl mb-1">{l.name}</h3>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] mb-3">{l.title}</div>
                {l.headline && <p className="text-[#1f3a60] text-[14px] mb-3 italic">{l.headline}</p>}
                <p className="text-[#212529]/85 text-[13.5px] leading-[1.7]">{l.bio}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   3. SERVICES / SECTORS
   ════════════════════════════════════════════════════════════════════ */
const SERVICE_ICONS = {
  factory:   Factory,
  handshake: Handshake,
  clock:     Clock,
  network:   Network,
  briefcase: Briefcase,
  users:     Users,
  shield:    ShieldCheck,
};

export function LifeServices() {
  const { cfg } = useOutletContext() || {};
  const [services, setServices] = useState([]);
  useEffect(() => {
    axios.get(`${API}/life-public/services`).then((r) => setServices(r.data?.services || [])).catch(() => {});
  }, []);
  useSeo({
    title:       "Services & Sectors · L.I.F.E., LLC",
    description: "Four specialized workforce-development tracks for corporate partners — Corporate Labor Optimization, Professional Relationship Development, Time Management Systems, and Community Network Integration.",
    canonical:   `${ORIG}/life-empowerment/services`,
    jsonLd:      defaultOrgJsonLd(cfg),
  });

  return (
    <div data-testid="life-page-services">
      <Container className="py-16 lg:py-24">
        <Eyebrow>Services & Sectors</Eyebrow>
        <h1 data-testid="life-services-title" className="life-serif font-medium italic text-[42px] sm:text-[56px] lg:text-[72px] text-[#1f3a60] tracking-tight leading-[1.05] mb-6 max-w-4xl">
          Four tracks. <span className="text-[#d4af37] not-italic font-semibold">One outcome.</span>
        </h1>
        <P className="text-[17px] mb-12">
          Each track exists to turn a community liability into a measurable
          business asset. Engagements are scoped per worksite, billed per hour,
          and audited per pay cycle.
        </P>

        <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
          {services.map((s) => {
            const Icon = SERVICE_ICONS[s.icon] || Briefcase;
            return (
              <article
                key={s.id}
                data-testid={`life-service-${s.slug}`}
                className="life-card group p-7 lg:p-9 relative overflow-hidden"
              >
                {/* Inner gold corner halo — subtle, intensifies on hover. */}
                <div className="absolute top-0 right-0 w-44 h-44 -mr-16 -mt-16 bg-gradient-to-br from-[#d4af37]/22 via-[#d4af37]/8 to-transparent rounded-full pointer-events-none transition-opacity duration-500 opacity-70 group-hover:opacity-100"/>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 grid place-items-center bg-gradient-to-br from-[#d4af37] to-[#c5a059] text-[#1f3a60] group-hover:scale-105 transition-all duration-300 shadow-sm">
                      <Icon className="h-6 w-6" strokeWidth={1.75}/>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] font-semibold">{s.eyebrow}</div>
                  </div>
                  <h2 className="life-serif font-semibold text-[#1f3a60] text-[28px] lg:text-[32px] mb-3 leading-[1.1]">{s.title}</h2>
                  <p className="text-[#212529] text-[14.5px] leading-[1.72] mb-4">{s.summary}</p>
                  {(s.bullets || []).length > 0 && (
                    <ul className="space-y-2">
                      {s.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13.5px] text-[#212529]">
                          <CheckCircle2 className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0"/> {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <P className="mx-auto mb-6 text-center">Looking for a custom scope? Engage us directly.</P>
          <PrimaryCta to="/life-empowerment/contact" testId="life-services-cta-partner">Secure Corporate Partnership</PrimaryCta>
        </div>
      </Container>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   4. RECOGNITION & METRICS — Cost Analysis scalability table
   ════════════════════════════════════════════════════════════════════ */
const COST_ANALYSIS = [
  { year: "Year 1 · 2023", gross: 233500,    capa: 60,    note: "Foundation year — local pilot." },
  { year: "Year 2 · 2024", gross: 607000,    capa: 120,   note: "Geographic expansion within South Carolina." },
  { year: "Year 3 · 2025", gross: 1639000,   capa: 240,   note: "Multi-state contracts; first equity partner cohort." },
  { year: "Year 4 · 2026", gross: 4573000,   capa: 480,   note: "Operating year — corporate partner roster doubled twice." },
  { year: "Year 5 · 2027", gross: 13111000,  capa: 960,   note: "Regional dominance · projected per Cost Analysis brief." },
];
const fmtUSD = (n) => `$${(n).toLocaleString("en-US")}`;

export function LifeRecognition() {
  const { cfg } = useOutletContext() || {};
  useSeo({
    title:       "Recognition & Multi-Year Metrics · L.I.F.E., LLC",
    description: "Multi-year scalability model for Learning Institute for Empowerment LLC — gross revenue projections, per-capita engagement growth, and equity-partner returns across five operating years.",
    canonical:   `${ORIG}/life-empowerment/recognition`,
    jsonLd:      defaultOrgJsonLd(cfg),
  });

  return (
    <div data-testid="life-page-recognition">
      <Container className="py-16 lg:py-24">
        <Eyebrow>Recognition & Multi-Year Metrics</Eyebrow>
        <h1 data-testid="life-recognition-title" className="life-serif font-medium italic text-[42px] sm:text-[56px] lg:text-[72px] text-[#1f3a60] tracking-tight leading-[1.05] mb-6 max-w-4xl">
          From <span className="text-[#d4af37] not-italic font-semibold">$233K</span> to <span className="text-[#b7092b] not-italic font-semibold">$13M+</span><br/>
          in five operating years.
        </h1>
        <P className="text-[17px] mb-12">
          The Empowered Human Element compounds. The Cost Analysis brief shows
          a scalability model where per-capita engagement and gross revenue
          both grow geometrically as L.I.F.E. expands from a Myrtle Beach
          foundation into a regional alliance.
        </P>

        {/* HERO YEAR-OVER-YEAR GRID */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-14">
          {COST_ANALYSIS.map((row, i) => (
            <div
              key={row.year}
              data-testid={`life-metric-year-${i+1}`}
              className="life-card p-5"
            >
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#b7092b] mb-2">{row.year}</div>
              <div className="font-display font-bold text-[#1f3a60] text-[26px] leading-none mb-2">{fmtUSD(row.gross)}</div>
              <div className="flex items-center gap-1 text-[11px] text-[#d4af37] mb-3 font-mono uppercase tracking-[0.18em] font-semibold">
                <TrendingUp className="h-3 w-3"/> {row.capa} per-capita
              </div>
              <p className="text-[#212529]/70 text-[12px] leading-relaxed">{row.note}</p>
            </div>
          ))}
        </div>

        {/* DETAILED REVENUE-MIX TABLE */}
        <div className="life-card overflow-x-auto">
          <table className="w-full text-[13px] text-[#1f3a60]" data-testid="life-recognition-table">
            <thead className="bg-[#f8f9fa] text-[#b7092b] font-mono text-[10px] tracking-[0.18em] uppercase border-b border-[#dee2e6]">
              <tr>
                <th className="text-left px-4 py-3">Year</th>
                <th className="text-right px-4 py-3">Gross Revenue</th>
                <th className="text-right px-4 py-3">Grant Funds (40%)</th>
                <th className="text-right px-4 py-3">Operational</th>
                <th className="text-right px-4 py-3">Equity Partner</th>
              </tr>
            </thead>
            <tbody>
              {COST_ANALYSIS.map((row) => {
                const grant = Math.round(row.gross * 0.40);
                const op    = Math.round(row.gross * 0.20);
                const eq    = Math.round(row.gross * 0.0375); // ~3.75% from cost analysis
                return (
                  <tr key={row.year} className="border-t border-[#dee2e6] hover:bg-[#f8f9fa]">
                    <td className="px-4 py-3 font-display font-semibold text-[#1f3a60]">{row.year}</td>
                    <td className="px-4 py-3 text-right text-[#b7092b] font-mono font-bold">{fmtUSD(row.gross)}</td>
                    <td className="px-4 py-3 text-right text-[#212529]">{fmtUSD(grant)}</td>
                    <td className="px-4 py-3 text-right text-[#212529]">{fmtUSD(op)}</td>
                    <td className="px-4 py-3 text-right text-[#212529]">{fmtUSD(eq)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[#212529]/55 text-[12px] mt-4 italic max-w-3xl">
          Projections derived from the canonical Cost Analysis brief on file
          with the corporate Identity Vault. Grant, operational, and equity-
          partner allocations are reconciled annually against the General
          Ledger held by the Senior Member.
        </p>
      </Container>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   5. CONTACT & INTAKE — partnership lead + contractor intake
   ════════════════════════════════════════════════════════════════════ */
export function LifeContact() {
  const { cfg } = useOutletContext() || {};
  const addr = cfg?.address?.single_line || "7433 Springside Dr, Myrtle Beach, SC 29588";
  const email = cfg?.public_email || "hello@life-empowerment.com";
  const [tab, setTab] = useState("partnership");
  useSeo({
    title:       "Contact & Intake · L.I.F.E., LLC · Myrtle Beach, SC",
    description: "Open a corporate partnership conversation, or submit a contractor intake application with a handwritten work schedule or ID upload. Routed directly to the Senior Member.",
    canonical:   `${ORIG}/life-empowerment/contact`,
    jsonLd:      defaultOrgJsonLd(cfg),
  });

  return (
    <div data-testid="life-page-contact">
      <Container className="py-16 lg:py-24">
        <Eyebrow>Contact · Partnership · Intake</Eyebrow>
        <h1 data-testid="life-contact-title" className="life-serif font-medium italic text-[42px] sm:text-[56px] lg:text-[72px] text-[#1f3a60] tracking-tight leading-[1.05] mb-6 max-w-4xl">
          Open a <span className="text-[#d4af37] not-italic font-semibold">conversation.</span>
        </h1>
        <P className="text-[17px] mb-10">
          Every inbound message is reviewed by the Senior Member. Corporate
          partners — start a partnership inquiry on the left. Vocational
          trainees and intake candidates — submit an application on the right.
          Uploaded work schedules and IDs are placed in a secure review queue
          (never auto-processed) and the Senior Member personally hands them
          off to the AI Timecard processor.
        </P>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8 lg:gap-10">
          <div>
            {/* Tab switch */}
            <div className="flex gap-1 mb-5 border-b border-[#dee2e6]" role="tablist">
              {[
                { k: "partnership", label: "Corporate Partnership" },
                { k: "intake",      label: "Contractor Intake" },
              ].map((t) => (
                <button
                  key={t.k}
                  role="tab"
                  type="button"
                  aria-selected={tab === t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`life-contact-tab-${t.k}`}
                  className={`px-4 py-2.5 font-mono text-[10.5px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors ${
                    tab === t.k
                      ? "text-[#b7092b] border-[#b7092b]"
                      : "text-[#1f3a60]/65 border-transparent hover:text-[#b7092b] hover:border-[#d4af37]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {tab === "partnership" ? <PartnershipForm /> : <IntakeForm />}
          </div>

          <aside className="life-card p-6 lg:p-8 h-fit">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#b7092b] mb-3">Headquarters</div>
            <div className="text-[#1f3a60] font-display font-semibold text-lg leading-snug mb-2">{addr.split(",")[0]}</div>
            <div className="text-[#212529]/85 text-sm leading-relaxed mb-6 flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0"/> {addr}
            </div>
            <div className="border-t border-[#dee2e6] pt-5 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-[#d4af37]"/>
                <a href={`mailto:${email}`} className="text-[#1f3a60] hover:text-[#b7092b]">{email}</a>
              </div>
              <div className="text-[#212529]/65 text-[12px] leading-relaxed">
                In-person appointments by reservation only. Please submit a
                partnership inquiry to schedule.
              </div>
            </div>
            <div className="border-t border-[#dee2e6] pt-5 mt-5 font-mono text-[10px] tracking-[0.18em] uppercase text-[#1f3a60]/65 leading-relaxed">
              {cfg?.legal_entity_name || "Learning Institute for Empowerment LLC"}<br/>
              SC Domestic LLC · Transaction ID <span className="text-[#b7092b] font-bold">{cfg?.sc_transaction_id || "1197551"}</span><br/>
              EIN <span className="text-[#b7092b] font-bold">{cfg?.ein || "92-1652917"}</span>
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}

function PartnershipForm() {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [form, setForm] = useState({
    organization: "", contact_name: "", contact_title: "",
    email: "", phone: "", workforce_size: "", inquiry_type: "partnership",
    message: "", hp: "",
  });
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/life-public/partnership-lead`, form);
      ga4Event("partnership_lead_submitted", { id: res.data?.id });
      setSent({ ok: true, id: res.data?.id });
      toast.success("Partnership inquiry received — we'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Try again in a moment.";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  if (sent?.ok) return (
    <div data-testid="life-partnership-success"
      className="life-card p-7 text-center">
      <CheckCircle2 className="h-10 w-10 text-[#d4af37] mx-auto mb-3"/>
      <h3 className="life-serif font-semibold text-2xl text-[#1f3a60] mb-2">Inquiry received.</h3>
      <p className="text-[#1f3a60]/75 text-sm leading-relaxed">
        The Senior Member will respond personally within two business days.
        Reference: <span className="font-mono text-[#d4af37]">{sent.id?.slice(0,8)}…</span>
      </p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="life-partnership-form" className="space-y-4">
      <input type="text" name="company_website" tabIndex="-1" autoComplete="off"
        value={form.hp} onChange={u("hp")}
        className="absolute -left-[10000px] opacity-0" aria-hidden="true"/>
      <Row>
        <Field label="Organization *"><Input value={form.organization} onChange={u("organization")} required testId="life-pf-org"/></Field>
        <Field label="Workforce Size">
          <Select value={form.workforce_size} onChange={u("workforce_size")} testId="life-pf-size">
            <option value="">Choose…</option>
            <option>1–50</option><option>51–250</option><option>251–1,000</option>
            <option>1,000+</option>
          </Select>
        </Field>
      </Row>
      <Row>
        <Field label="Your Name *"><Input value={form.contact_name} onChange={u("contact_name")} required testId="life-pf-name"/></Field>
        <Field label="Title"><Input value={form.contact_title} onChange={u("contact_title")} testId="life-pf-title"/></Field>
      </Row>
      <Row>
        <Field label="Email *"><Input type="email" value={form.email} onChange={u("email")} required testId="life-pf-email"/></Field>
        <Field label="Phone"><Input value={form.phone} onChange={u("phone")} testId="life-pf-phone"/></Field>
      </Row>
      <Field label="What does success look like for your worksite? *">
        <textarea required value={form.message} onChange={u("message")} rows={5}
          data-testid="life-pf-message"
          className="w-full bg-white border border-[#dee2e6] focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/30 outline-none px-3 py-2.5 text-[14px] text-[#1f3a60] rounded-sm transition-colors"/>
      </Field>
      <button type="submit" disabled={busy} data-testid="life-pf-submit"
        className="life-cta-gold inline-flex items-center gap-2 px-6 py-3 font-mono text-[10.5px] tracking-[0.24em] uppercase font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
        Send Partnership Inquiry
      </button>
    </form>
  );
}

function IntakeForm() {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    role_interest: "vocational_trainee", notes: "", hp: "",
  });
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ── Drag-over → drop-zone illuminates in brushed gold ──
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (!dropped) return;
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(dropped.type)) {
      toast.error("Unsupported file type. PDF, PNG, JPG, or WebP only.");
      return;
    }
    if (dropped.size > 12 * 1024 * 1024) {
      toast.error("File too large. 12 MB maximum.");
      return;
    }
    setFile(dropped);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file, file.name);
      const res = await axios.post(`${API}/life-public/intake`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      ga4Event("intake_submitted", { id: res.data?.id, with_file: !!file });
      setSent({ ok: true, id: res.data?.id });
      toast.success("Intake received — placed in the secure review queue.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  if (sent?.ok) return (
    <div data-testid="life-intake-success"
      className="life-card p-7 text-center">
      <FileCheck2 className="h-10 w-10 text-[#d4af37] mx-auto mb-3"/>
      <h3 className="life-serif font-semibold text-2xl text-[#1f3a60] mb-2">Intake received.</h3>
      <p className="text-[#1f3a60]/75 text-sm leading-relaxed">
        Your application is queued for personal review by the Senior Member.
        Reference: <span className="font-mono text-[#d4af37]">{sent.id?.slice(0,8)}…</span>
      </p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="life-intake-form" className="space-y-4">
      <input type="text" name="company_website" tabIndex="-1" autoComplete="off"
        value={form.hp} onChange={u("hp")}
        className="absolute -left-[10000px] opacity-0" aria-hidden="true"/>
      <Row>
        <Field label="Full Legal Name *"><Input value={form.full_name} onChange={u("full_name")} required testId="life-if-name"/></Field>
        <Field label="Role of Interest">
          <Select value={form.role_interest} onChange={u("role_interest")} testId="life-if-role">
            <option value="vocational_trainee">Vocational Trainee</option>
            <option value="contractor">Independent Contractor</option>
            <option value="program_participant">Program Participant</option>
            <option value="other">Other</option>
          </Select>
        </Field>
      </Row>
      <Row>
        <Field label="Email *"><Input type="email" value={form.email} onChange={u("email")} required testId="life-if-email"/></Field>
        <Field label="Phone"><Input value={form.phone} onChange={u("phone")} testId="life-if-phone"/></Field>
      </Row>
      <Field label="Tell us about your background and what you're seeking">
        <textarea value={form.notes} onChange={u("notes")} rows={4}
          data-testid="life-if-notes"
          className="w-full bg-white border border-[#dee2e6] focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/30 outline-none px-3 py-2.5 text-[14px] text-[#1f3a60] rounded-sm transition-colors"/>
      </Field>

      {/* 🔒 High-security file dropzone · dotted charcoal → brushed gold on drag-over */}
      <Field label="Attach a handwritten work schedule or photo ID (optional · PDF / PNG / JPG / WebP · 12 MB max)">
        <label
          data-testid="life-if-dropzone"
          data-drag-active={dragActive ? "true" : "false"}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className="life-dropzone block px-5 py-7 cursor-pointer text-center group"
        >
          <input type="file" accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp"
            data-testid="life-if-file" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}/>
          <div className="flex flex-col items-center gap-2">
            <div className={`h-12 w-12 grid place-items-center border transition-all duration-300 rounded-full ${
              dragActive
                ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37]/30 to-[#c5a059]/20 scale-110"
                : "border-[#dee2e6] bg-white group-hover:border-[#1f3a60]/35"
            }`}>
              <UploadCloud className={`h-5 w-5 transition-colors ${dragActive ? "text-[#b7092b]" : "text-[#1f3a60]/70 group-hover:text-[#b7092b]"}`}/>
            </div>
            {file ? (
              <div className="flex flex-col items-center gap-1">
                <span className="text-[14px] text-[#1f3a60] font-semibold truncate max-w-[40ch]">{file.name}</span>
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#b7092b]">
                  {(file.size/1024).toFixed(1)} KB · secured for review
                </span>
                <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }}
                  className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#1f3a60]/60 hover:text-[#b7092b]">
                  Remove file
                </button>
              </div>
            ) : (
              <>
                <div className={`font-mono text-[10.5px] tracking-[0.24em] uppercase transition-colors font-semibold ${
                  dragActive ? "text-[#b7092b]" : "text-[#1f3a60]"
                }`}>
                  {dragActive ? "Release to upload · secure portal armed" : "Drag & drop or click to select"}
                </div>
                <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-[#1f3a60]/55">
                  Encrypted · SA-only review · No auto-processing
                </div>
              </>
            )}
          </div>
        </label>
      </Field>
      <div className="flex items-start gap-2 text-[11.5px] text-[#1f3a60]/55 leading-relaxed">
        <AlertCircle className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0"/>
        <span>Files are placed in a secure review queue. The Senior Member personally hands off approved submissions to the AI Timecard processor — nothing runs automatically.</span>
      </div>
      <button type="submit" disabled={busy} data-testid="life-if-submit"
        className="life-cta-gold inline-flex items-center gap-2 px-6 py-3 font-mono text-[10.5px] tracking-[0.24em] uppercase font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
        Submit Intake Application
      </button>
    </form>
  );
}

/* ─── form atoms ──────────────────────────────────────────────────── */
function Row({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9.5px] tracking-[0.22em] uppercase text-[#1f3a60]/70 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}
function Input({ testId, ...rest }) {
  return <input data-testid={testId}
    className="w-full bg-white border border-[#dee2e6] focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/30 outline-none px-3 py-2.5 text-[14px] text-[#1f3a60] rounded-sm transition-colors"
    {...rest}/>;
}
function Select({ testId, children, ...rest }) {
  return (
    <select data-testid={testId}
      className="w-full bg-white border border-[#dee2e6] focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/30 outline-none px-3 py-2.5 text-[14px] text-[#1f3a60] rounded-sm transition-colors"
      {...rest}>{children}</select>
  );
}

/* ════════════════════════════════════════════════════════════════════
   6. PRIVACY  /  7. TERMS
   ════════════════════════════════════════════════════════════════════ */
function LegalShell({ title, eyebrow, children, testId, canonical }) {
  const { cfg } = useOutletContext() || {};
  useSeo({
    title:       `${title} · L.I.F.E., LLC`,
    description: `${title} for Learning Institute for Empowerment LLC — a South Carolina domestic LLC.`,
    canonical,
    jsonLd:      defaultOrgJsonLd(cfg),
  });
  return (
    <div data-testid={testId}>
      <Container className="py-16 lg:py-24 max-w-4xl">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1f3a60] tracking-tight leading-[1.05] mb-6">{title}</h1>
        <div className="prose max-w-none [&_h2]:life-serif [&_h2]:font-semibold [&_h2]:text-2xl [&_h2]:text-[#1f3a60] [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-[#b7092b] [&_h3]:font-mono [&_h3]:text-xs [&_h3]:tracking-[0.22em] [&_h3]:uppercase [&_p]:text-[#212529] [&_p]:leading-[1.78] [&_p]:text-[15px] [&_li]:text-[#212529] [&_li]:text-[14.5px] [&_li]:leading-[1.72] [&_strong]:text-[#1f3a60] [&_a]:text-[#b7092b] [&_a]:underline-offset-2 hover:[&_a]:underline">
          {children}
        </div>
      </Container>
    </div>
  );
}

export function LifePrivacy() {
  return (
    <LegalShell
      title="Privacy Policy" eyebrow="Legal · Privacy"
      testId="life-page-privacy"
      canonical={`${ORIG}/life-empowerment/privacy`}
    >
      <p><strong>Effective Date:</strong> {new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}.</p>
      <p>
        Learning Institute for Empowerment LLC ("L.I.F.E.", "we", "our") respects your privacy. This Privacy Policy explains what information we collect when you visit our public marketing site at <em>/life-empowerment</em>, what we do with it, and the choices you have.
      </p>

      <h2>1. Information we collect</h2>
      <p>We collect only the minimum information needed to operate this site and respond to inquiries:</p>
      <ul>
        <li><strong>Form submissions</strong> — when you submit a partnership inquiry or intake application, we receive the contact and message information you provide, including any optional file uploads.</li>
        <li><strong>Optional analytics</strong> — if you accept the cookie banner, we use Google Analytics 4 to collect anonymized session metrics (pages viewed, approximate location, referrer). Declining the banner suppresses this entirely.</li>
        <li><strong>Server logs</strong> — IP address and user-agent are recorded with each form submission for abuse prevention and audit. These logs are retained for 90 days.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To respond to your partnership inquiry or intake application.</li>
        <li>To improve the public site and our published service offerings.</li>
        <li>To detect and prevent abuse (rate-limiting, honeypot detection).</li>
      </ul>

      <h2>3. How we DO NOT use your information</h2>
      <ul>
        <li>We do not sell your personal information to third parties.</li>
        <li>We do not run ad networks or behavioral retargeting on this site.</li>
        <li>We do not auto-process uploaded files — every intake is reviewed by the Senior Member personally before any downstream pipeline runs.</li>
      </ul>

      <h2>4. Data retention &amp; deletion</h2>
      <p>Partnership leads and intake records are retained for the lifetime of our business relationship with you. You can request deletion at any time by emailing the address in the footer; we will honor the request within 30 days, subject to any legal hold.</p>

      <h2>5. Your rights</h2>
      <p>Depending on your jurisdiction, you may have the right to access, correct, or delete the personal information we hold about you. Contact us to exercise any of these rights.</p>

      <h2>6. Contact</h2>
      <p>Questions about this policy can be sent to <a className="text-[#d4af37]" href="mailto:hello@life-empowerment.com">hello@life-empowerment.com</a>.</p>
    </LegalShell>
  );
}

export function LifeTerms() {
  return (
    <LegalShell
      title="Terms of Service" eyebrow="Legal · Terms"
      testId="life-page-terms"
      canonical={`${ORIG}/life-empowerment/terms`}
    >
      <p><strong>Effective Date:</strong> {new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}.</p>
      <p>
        These Terms of Service ("Terms") govern your use of the L.I.F.E., LLC public marketing site at <em>/life-empowerment</em>. By using the site you agree to these Terms.
      </p>

      <h2>1. Use of the site</h2>
      <p>You may browse the site for information about L.I.F.E.'s services, leadership, and partnership opportunities. You may not attempt to interfere with the site's operation, scrape it at unreasonable volumes, or upload content that is unlawful, defamatory, or that you do not have the right to share.</p>

      <h2>2. Submissions &amp; uploads</h2>
      <p>When you submit a partnership inquiry, intake application, or file upload, you represent that the information is accurate and that you have the right to share it. You retain ownership of materials you upload, but you grant L.I.F.E. a limited license to review, store, and route them within the secure review queue for the purpose of evaluating your inquiry.</p>

      <h2>3. No professional advice</h2>
      <p>Content on this site is informational only and does not constitute legal, tax, or financial advice. Always consult a qualified professional for your specific circumstances.</p>

      <h2>4. Intellectual property</h2>
      <p>The L.I.F.E. emblem, brand marks, copy, and original imagery are property of Learning Institute for Empowerment LLC. You may not reproduce them without written permission.</p>

      <h2>5. Disclaimers &amp; limitation of liability</h2>
      <p>The site is provided "as is", without warranties of any kind. To the maximum extent permitted by law, L.I.F.E. is not liable for any indirect, incidental, or consequential damages arising from your use of the site.</p>

      <h2>6. Governing law</h2>
      <p>These Terms are governed by the laws of the State of South Carolina, without regard to its conflict of laws provisions. Any disputes will be resolved in the state or federal courts located in Horry County, South Carolina.</p>

      <h2>7. Contact</h2>
      <p>Questions about these Terms can be sent to <a className="text-[#d4af37]" href="mailto:hello@life-empowerment.com">hello@life-empowerment.com</a>.</p>
    </LegalShell>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Sparkles import marker (no-op to keep tree-shake happy with the
   conditional usage in LifeContact aside).
   ════════════════════════════════════════════════════════════════════ */
export const _spark = Sparkles; // referenced for completeness
